import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';

const mergedTagParamsSchema = projectIdParamsSchema.extend({
	tagId: requiredStringSchema.describe('Tag identifier'),
});

/**
 * Merged tags. `{ root: true }` so `/projects/:projectId` can PRECEDE the
 * collection segment, which a contract-name prefix cannot express. Both paths are
 * therefore spelled absolutely.
 *
 * **The project field is per-OPERATION here, and this codifies what the service
 * already did rather than changing it.** A merged tag carries two projects:
 * `originProjectId`, the project that OWNS the tag (immutable, copied from the
 * static tag), and `projectId`, the project the tag is currently IN — re-derived
 * on every uplink from the anchor it is nearest to, with an explicit "Tag project
 * changed" branch in `mergedTags.service.ts`.
 *
 * | Operation                          | Field              | Why                       |
 * | ---------------------------------- | ------------------ | ------------------------- |
 * | roster read (`getAllTags`) + admin panel | `originProjectId` | **ownership** — a project sees its own tags even while they are physically elsewhere |
 * | by-id read (`getMergedTagById`)    | `projectId`        | **presence** — the realtime view is about where the tag IS |
 *
 * The admin panel is the reason: it renders `lastSeenProjectId:
 * currentMergedTag.projectId` and resolves a DIFFERENT project's name for it,
 * which is only meaningful if the roster is the owning project's. This is the
 * `professions` ownership-vs-reachability split with different field names, and
 * it is deliberate on both sides.
 */
export const mergedTagsContract = defineContract(
	'mergedTags',
	{
		/**
		 * OWNERSHIP: `originProjectId`. This is a project's roster of its own tags,
		 * and it is what `getAdminPanelTagsData` reads. Scoping it on `projectId`
		 * would empty the admin panel of every tag currently on another site.
		 */
		getAllTags: route({
			method: 'GET',
			path: '/projects/:projectId/mergedTags',
			params: projectIdParamsSchema,
			response: z.array(z.any()),
			operationId: 'getAllTags',
			tags: ['Merged Tags'],
			summary: 'Get the merged tags a project owns',
		}),
		/**
		 * PRESENCE: `projectId`. A by-id read answers "what is this tag doing right
		 * now", so the project that may see it is the one it is currently in.
		 * Scoping it on `originProjectId` would leak a tag that has physically moved
		 * to another site back to its former owner's live view.
		 */
		getMergedTagById: route({
			method: 'GET',
			path: '/projects/:projectId/mergedTags/:tagId',
			params: mergedTagParamsSchema,
			response: z.any(),
			operationId: 'getMergedTagById',
			tags: ['Merged Tags'],
			summary: 'Get a merged tag currently in this project, by tag ID',
		}),
	},
	{ root: true }
);
